import { useContext } from 'react';

import WorkOrderDatePicker from '@atom/components/common/workOrderDetail/workOrderFields/WorkOrderDatePicker';
import WorkOrderEnumMultipleField from '@atom/components/common/workOrderDetail/workOrderFields/WorkOrderEnumMultipleField';
import WorkOrderEnumMultipleFieldList from '@atom/components/common/workOrderDetail/workOrderFields/WorkOrderEnumMultipleFieldList';
import WorkOrderEnumSingleField from '@atom/components/common/workOrderDetail/workOrderFields/WorkOrderEnumSingleField';
import WorkOrderEnumSingleFieldList from '@atom/components/common/workOrderDetail/workOrderFields/WorkOrderEnumSingleFieldList';
import WorkOrderHyperlinkField from '@atom/components/common/workOrderDetail/workOrderFields/WorkOrderHyperlinkField';
import WorkOrderNumericField from '@atom/components/common/workOrderDetail/workOrderFields/WorkOrderNumericField';
import WorkOrderSummary from '@atom/components/common/workOrderDetail/workOrderFields/WorkOrderSummary';
import WorkOrderTextField from '@atom/components/common/workOrderDetail/workOrderFields/WorkOrderTextField';
import { DataType, FieldViewType } from '@atom/types/dataType';
import { WorkOrderBulkField, WorkOrderField } from '@atom/types/work';
import { CustomFieldView } from '@atom/utilities/taskUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import CreateWorkModalContext from '../../CreateWorkModalContext';

import classes from './workDetails.module.css';

type CustomFieldRowProps = {
  field: WorkOrderBulkField;
  onChange: (fieldId: string, value: any) => void;
};

const CustomFieldRow = ({ field, onChange }: CustomFieldRowProps) => {
  const { requiredFieldIds } = useContext(CreateWorkModalContext);

  const props = {
    field: field as WorkOrderField,
    onChange,
    onBlur: onChange,
    isDisabled: false,
    customFieldView: CustomFieldView.DETAIL,
    requiredFieldIds,
    requiredFieldName: 'requiredOnCreate' as keyof WorkOrderField,
  };

  const hasSubFields = !isNilOrEmpty(field.subFields);

  const isListView = field.viewAs === FieldViewType.CHECKBOX;

  const components = {
    [DataType.SUMMARY]: <WorkOrderSummary {...props} width="100%" />,
    [DataType.SHORT_TEXT]: <WorkOrderTextField {...props} width="100%" />,
    [DataType.LONG_TEXT]: <WorkOrderTextField {...props} width="100%" />,
    [DataType.DATE]: <WorkOrderDatePicker {...props} width="50%" />,
    [DataType.DATE_TIME]: <WorkOrderDatePicker {...props} width="50%" />,
    [DataType.ENUM_SINGLE]: hasSubFields ? (
      <div className={classes.enumSingleNested}>
        <WorkOrderEnumSingleField {...props} />
      </div>
    ) : isListView ? (
      <WorkOrderEnumSingleFieldList {...props} />
    ) : (
      <WorkOrderEnumSingleField {...props} width="100%" />
    ),
    [DataType.ENUM_MULTIPLE]: isListView ? (
      <WorkOrderEnumMultipleFieldList {...props} />
    ) : (
      <WorkOrderEnumMultipleField {...props} width="100%" />
    ),
    [DataType.NUMBER]: <WorkOrderNumericField {...props} width="50%" />,
    [DataType.HYPERLINK]: <WorkOrderHyperlinkField {...props} width="100%" />,
  };

  return (
    <div
      className={classes.fieldContainer}
      data-cy={`createWorkModalCustomFieldContainer-${field.title}`}
    >
      {components[field.dataType]}
    </div>
  );
};

export default CustomFieldRow;
